import React from 'react'
import Menu, { MenuProps } from 'components/menu'
import {
  Filter,
  Sort,
  Group,
  Visible,
  Colour,
  ImportData,
  Join,
  History,
  Lock,
  Asterisk,
  MoveColumn,
  Freeze,
  SmallRowHeight,
  Chart,
  Link as LinkIcon,
  Grid,
  Search,
  Save,
  ExportData
} from 'components/icons'
import { ViewTypes } from 'components/spreadsheet/constants/const'
import { getViewCapabilities, isCapabilityEnabled } from 'components/spreadsheet/constants/viewCapabilities'
import { HUB_BASE } from 'routes'
import { MenuHeader, MenuItem, MenuLabel, MenuList, MenuSeparator } from 'components/menu/items'
import useProject from 'hooks/useProject'

interface ViewConfigMenuProps extends MenuProps {
  tableId: string
  viewId: string
  viewType: ViewTypes
  isAdmin: boolean
  isDeleted: boolean
  /** Whether Import Data is offered on this view (`canImportData`), as the import button is. */
  canImport: boolean
  inProcess: boolean
  isPersonalView: boolean
  allowExport: boolean
  onFilterClick: () => void
  onSortClick: () => void
  onGroupClick: () => void
  onHideClick: () => void
  onColourClick: () => void
  onImportClick: () => void
  onExportClick: () => void
  /** Saving the view's changes. Left out where the view can't be saved from here. */
  onSaveClick?: () => void
  /** Shown muted and not clickable, as the save button is, when saving is blocked. */
  saveDisabled?: boolean
  onJoinsClick: () => void
  onHistoryClick: () => void
  onLockClick: () => void
  onRequiredClick: () => void
  onColumnOrderClick: () => void
  onFreezeClick: () => void
  onRowHeightClick: () => void
  onChartClick: () => void
  onMatrixClick: () => void
  onPivotClick?: () => void
  onSearchClick?: () => void
  /**
   * Just these items (by key: 'filter', 'lock', …), under their groups'
   * labels: the table page's View actions menu, which lists every action of
   * the view row, labelled, whether or not the row has room to show it too.
   * Without it, every item that applies shows, unlabelled.
   */
  groups?: { label: string; items: string[] }[]
  /** Which actions are on (by the same keys): their rows end in a blue dot, as View actions' own. */
  active?: Record<string, boolean>
}

const ViewConfigMenu: React.FC<ViewConfigMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  tableId,
  viewId,
  viewType,
  isAdmin,
  isDeleted,
  canImport,
  inProcess,
  isPersonalView,
  allowExport,
  onFilterClick,
  onSortClick,
  onGroupClick,
  onHideClick,
  onColourClick,
  onImportClick,
  onExportClick,
  onSaveClick,
  saveDisabled,
  onJoinsClick,
  onHistoryClick,
  onLockClick,
  onRequiredClick,
  onColumnOrderClick,
  onFreezeClick,
  onRowHeightClick,
  onChartClick,
  onMatrixClick,
  onPivotClick,
  onSearchClick,
  groups,
  active = {}
}) => {
  const { project } = useProject()

  const capabilities = getViewCapabilities(viewType)
  const isForm = viewType === ViewTypes.FORM

  // Which items apply here, whatever's asked for.
  const applies: Record<string, boolean> = {
    chart: !!(capabilities.toolbar.chartSettings && !isDeleted),
    matrix: !!(capabilities.toolbar.matrixSettings && !isDeleted),
    pivot: !!(onPivotClick && capabilities.toolbar.pivotSettings && !isDeleted),
    search: !!(onSearchClick && viewType === ViewTypes.SPREADSHEET),
    colour: !!(!isDeleted && capabilities.toolbar.colour),
    filter: !!(!isDeleted && capabilities.toolbar.filter),
    sort: !!(!isDeleted && capabilities.toolbar.sort),
    group: !!(!isDeleted && capabilities.toolbar.group),
    hide: !!(!isDeleted && isCapabilityEnabled(capabilities.toolbar.hide, isAdmin)),
    import: canImport,
    export: !!(capabilities.toolbar.exportData && allowExport),
    save: !!onSaveClick,
    joins: !!(!isDeleted && isCapabilityEnabled(capabilities.toolbar.joins, isAdmin)),
    history: !!(isAdmin && !isDeleted),
    lock: !!(isAdmin && capabilities.toolbar.lock),
    required: !!(isAdmin && isForm),
    columnOrder: !!(!isDeleted && capabilities.toolbar.columnOrder),
    freeze: !!(!isDeleted && capabilities.toolbar.freeze),
    rowHeight: !!(!isDeleted && capabilities.toolbar.rowHeight),
    link: !!(inProcess && isAdmin && !isPersonalView)
  }
  const close = () => setMenuState({ open: false, top: 'auto', left: 'auto', right: 'auto', bottom: 'auto' })

  // Every item, in the order the menu lists them when it isn't given groups.
  const items: Record<
    string,
    { icon: React.ReactNode; label: string; onClick?: () => void; disabled?: boolean; to?: string }
  > = {
    chart: {
      icon: <Chart />,
      label: viewType === ViewTypes.CALENDAR ? 'Change calendar settings' : 'Change chart settings',
      onClick: onChartClick
    },
    matrix: { icon: <Chart />, label: 'Configure matrix view', onClick: onMatrixClick },
    pivot: { icon: <Grid />, label: 'Change pivot column', onClick: onPivotClick },
    search: { icon: <Search />, label: 'Search for data', onClick: onSearchClick },
    colour: { icon: <Colour />, label: 'Conditional formatting', onClick: onColourClick },
    filter: { icon: <Filter />, label: 'Filter rows', onClick: onFilterClick },
    sort: { icon: <Sort />, label: 'Sort rows', onClick: onSortClick },
    group: { icon: <Group />, label: 'Group rows', onClick: onGroupClick },
    hide: { icon: <Visible />, label: 'Hide columns', onClick: onHideClick },
    import: { icon: <ImportData />, label: 'Import data', onClick: onImportClick },
    export: { icon: <ExportData />, label: 'Export data', onClick: onExportClick },
    save: { icon: <Save />, label: 'Save changes to view', onClick: onSaveClick, disabled: saveDisabled },
    joins: { icon: <Join />, label: 'Lookup data from other tables', onClick: onJoinsClick },
    history: { icon: <History />, label: 'Audit history and backups', onClick: onHistoryClick },
    lock: { icon: <Lock />, label: 'Lock rows', onClick: onLockClick },
    required: { icon: <Asterisk />, label: 'Required fields', onClick: onRequiredClick },
    columnOrder: { icon: <MoveColumn />, label: 'Change column order', onClick: onColumnOrderClick },
    freeze: { icon: <Freeze />, label: 'Freeze columns in place', onClick: onFreezeClick },
    rowHeight: { icon: <SmallRowHeight />, label: 'Change row height', onClick: onRowHeightClick },
    link: {
      icon: <LinkIcon />,
      label: 'Open in new tab',
      to: `${HUB_BASE}${project.publicId}/table/${tableId}/view/${viewId}`
    }
  }

  const row = (key: string) => {
    const item = items[key]
    return (
      <MenuItem
        key={key}
        icon={item.icon}
        disabled={item.disabled}
        end={active[key] ? <ActiveDot /> : undefined}
        // A new tab, so the row is a link rather than an action that closes the menu.
        {...(item.to
          ? { to: item.to, onClick: (event) => openInNewTab(event, item.to!) }
          : {
              onClick: () => {
                close()
                item.onClick?.()
              }
            })}
      >
        {item.label}
      </MenuItem>
    )
  }

  // Given groups, the menu lists just their items, in their order, each group under its label and
  // apart from the one before (a group with nothing that applies here, not at all). Without them,
  // every item that applies, unlabelled.
  const shownGroups = (groups ?? [])
    .map((group) => ({ label: group.label, keys: group.items.filter((key) => applies[key]) }))
    .filter((group) => group.keys.length > 0)

  return (
    <Menu id={id} menuState={menuState} setMenuState={setMenuState} width={width} zIndex={3000}>
      <MenuHeader>View settings</MenuHeader>
      <MenuList>
        {groups
          ? shownGroups.map((group, index) => (
              <React.Fragment key={group.label}>
                {index > 0 && <MenuSeparator />}
                <MenuLabel>{group.label}</MenuLabel>
                {group.keys.map(row)}
              </React.Fragment>
            ))
          : Object.keys(items)
              .filter((key) => applies[key])
              .map(row)}
      </MenuList>
    </Menu>
  )
}

// A row that's a link opens its page in a new tab, as the old row's link did.
const openInNewTab = (event: React.SyntheticEvent<HTMLElement>, to: string) => {
  event.preventDefault()
  window.open(to, '_blank', 'noopener,noreferrer')
}

export default ViewConfigMenu

// The dot that marks an action that's on: the same as the one on the All view actions button.
const ActiveDot: React.FC = () => (
  <>
    <span aria-hidden className="size-1.5 rounded-full bg-blue" data-testid="view-action-on" />
    <span className="sr-only">On</span>
  </>
)
